/**
 * `RuntimeState.randomness` — the deterministic PRNG state (architecture.md
 * §3.4, decision D08).
 *
 * @remarks
 * This schema is the type source for `@orpheus/engine-runtime`'s mulberry32
 * module (Mike's R1 ruling, 2026-08-12): the engine imports the inferred
 * {@link RandomnessState} and keeps the pure draw helpers. `seed` is the
 * current internal uint32 state (advances with each draw); `drawCount` is the
 * audit trail — replay divergence shows up there first; `sessionSeed` (D141)
 * is the root the session started from and never moves — the thing a
 * derived, order-independent shuffle (a voice pool's permutation) is keyed on,
 * so that a draw elsewhere in the turn cannot reshuffle it.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { nonNegativeIntegerSchema, randomSeedSchema } from '../primitives.js';

/** The serializable mulberry32 state carried inside `RuntimeState.randomness`. */
export const randomnessStateSchema = z
	.strictObject({
		drawCount: nonNegativeIntegerSchema,
		seed: randomSeedSchema,
		sessionSeed: randomSeedSchema,
	})
	.readonly();

/** The `randomness` sub-state — see {@link randomnessStateSchema}. */
export type RandomnessState = z.infer<typeof randomnessStateSchema>;
